import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import { isEqual } from 'lodash';

import Wizard, { wizardTypes } from '../containers/Modal/Wizard';
import { DateTimePickerField, Input, SelectField } from '../containers/Form';
import Strings from '../Strings';
import { closeModal } from '../actions/modal';
import { actions } from '../pages/SuperUser/Patients/redux/actions';
import { ADD_ANNOTATION_ERROR, DELETE_ANNOTATION_ERROR, EDIT_ANNOTATION_ERROR } from '../pages/SuperUser/Patients/redux/constants';
import { notificationActions } from '../components/Notification/redux/actions';
import { allVitals, getVitalsFromValues } from '../utils/enrollmentFields';
import './OnboardPatientWizard/OnboardPatientWizard.scss';
import './EditAnnotationModal.scss';

export const EDIT_ANNOTATIONS_MODAL_NAME = 'edit-annotations-modal';

function EditAnnotationsModal(props) {
  const { showNotification, closeModalWithNextAction, data, addAnnotation, editAnnotation, getAnnotations, deleteAnnotation } = props;
  const { patientId } = data || {};

  const [annotations, setAnnotations] = React.useState([]);
  const [originalAnnotations, setOriginalAnnotations] = React.useState([]);
  const [validationErrors, setValidationErrors] = React.useState([]);
  const [highlightInvalidFields, setHighlightMissingFields] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    getAnnotations(patientId).then(resp => {
      const fetchedAnnotations = resp.response?.annotations
        ? resp.response.annotations.map(a => {
          const vitals = getVitalsFromValues(a.vital_types);
          return {
            ...a, datetime: moment(a.datetime), vitals: vitals?.[0]};
        })
        : [];
      setAnnotations(fetchedAnnotations);
      setOriginalAnnotations(fetchedAnnotations.map(a => ({ ...a, datetime: a.datetime.clone() })));
    });
  }, [patientId, getAnnotations]);

  React.useEffect(() => {
    const errors = [];
    annotations.forEach((a, i) => {
      if (!a.text) {
        errors.push({ property: "text", index: i, propertyName: Strings.title, missing: true });
      }
      if (!a.datetime) {
        errors.push({ property: "datetime", index: i, propertyName: Strings.date, missing: true });
      }
      if (!a.format?.color) {
        errors.push({ property: "format", index: i, propertyName: Strings.color, missing: true });
      }
    });
    setValidationErrors(errors);
  }, [annotations]);

  const onSave = async () => {
    if (isSaving) {
      return;
    }
    setIsSaving(true);
    let success = true;
    const newAnnotations = annotations.filter(a => !a.id);
    await Promise.all(newAnnotations.map(async a => {
      const res = await addAnnotation(patientId, {...a, datetime: a.datetime.format(), vital_types: a.vitals ? a.vitals?.vitals : undefined});
      if (res?.type === ADD_ANNOTATION_ERROR) {
        success = false;
        showNotification(res.response.data?.error?.message, 5000, true);
      }
    }));
    const changedAnnotations = annotations.filter(a => a.id && !isEqual(a, originalAnnotations.find(oa => oa.id === a.id)));
    await Promise.all(changedAnnotations.map(async a => {
      const res = await editAnnotation(patientId, a.id, {...a, datetime: a.datetime.format(), vital_types: a.vitals ? a.vitals?.vitals : undefined});
      if (res?.type === EDIT_ANNOTATION_ERROR) {
        success = false;
        showNotification(res.response.data?.error?.message, 5000, true);
      }
    }));
    const deletedAnnotations = originalAnnotations.filter(a => a.id).filter(a => !annotations.some(ann => ann.id === a.id));
    await Promise.all(deletedAnnotations.map(async a => {
      const res = await deleteAnnotation(patientId, a.id);
      if (res?.type === DELETE_ANNOTATION_ERROR) {
        success = false;
        showNotification(res.response.data?.error?.message, 5000, true);
      }
    }));
    if (!success) {
      setIsSaving(false);
      return;
    }
    closeModalWithNextAction();
    setIsSaving(false);
  };

  const onAnnotationChange = (field, index, value) => {
    const newAnnotations = [...annotations];
    newAnnotations[index][field] = value;
    setAnnotations(newAnnotations);
  };

  const pages = [
    {
      id: 'edit-annotations-page',
      name: Strings.annotations,
      content: (
        <>
          {annotations?.map((a, i) => (
            <React.Fragment key={i}>
              <Input
                id={`annotation-text-${i}`}
                name={`annotation-text-${i}`}
                type="text"
                label={Strings.title}
                value={a.text}
                onChange={e => onAnnotationChange('text', i, e.target.value)}
                isRequired
                highlightInvalid={highlightInvalidFields}
                errorsForTooltip={validationErrors.find(v => v.property === 'text' && v.index === i)?.errors || []}
                data-testid={`annotation-text-${i}`}
              />
              <DateTimePickerField
                id={`annotation-datetime-${i}`}
                name={`annotation-datetime-${i}`}
                label={Strings.date}
                value={a.datetime}
                onChange={date => onAnnotationChange('datetime', i, date)}
                isRequired
                highlightInvalid={highlightInvalidFields}
                errorsForTooltip={validationErrors.find(v => v.property === 'datetime' && v.index === i)?.errors || []}
                data-testid={`annotation-datetime-${i}`}
              />
              <SelectField
                id={`annotation-vital-types-${i}`}
                label={Strings.vitals}
                data={allVitals}
                value={a.vitals}
                onChange={o => onAnnotationChange('vitals', i, o)}
                highlightInvalid={highlightInvalidFields}
                errorsForTooltip={validationErrors.find(v => v.property === 'vitals' && v.index === i)?.errors || []}
                data-testid={`annotation-vitals-${i}`}
              />
              <Input
                id={`annotation-color-${i}`}
                type="color"
                label={Strings.color}
                value={a.format.color}
                onChange={e => onAnnotationChange('format', i, { ...a.format, color: e.target.value })}
                isRequired
                highlightInvalid={highlightInvalidFields}
                errorsForTooltip={validationErrors.find(v => v.property === 'format' && v.index === i)?.errors || []}
                data-testid={`annotation-color-${i}`}
              />
              <div className="button-container ">
                <button
                  className="minus-button high"
                  onClick={() => setAnnotations(annotations.filter((_, index) => index !== i))}
                  data-testid={`remove-annotation-${i}`}
                />
              </div>
            </React.Fragment>
          ))}
          <div className="button-container ">
            <button
              className="plus-button"
              onClick={() => setAnnotations([...annotations, { text: '', datetime: moment(), vital_types: [], format: { color: '#000000' } }])}
              data-testid="add-annotation"
            />
          </div>
        </>
      ),
      emptyFieldsCount: validationErrors.filter(v => v.missing).length,
      canGoNext: validationErrors.length === 0 && !isSaving,
      nextButton: { text: Strings.save },
    },
  ];

  return (
    <Wizard
      name={EDIT_ANNOTATIONS_MODAL_NAME}
      type={wizardTypes.horizontal}
      title={Strings.editAnnotations}
      pages={pages}
      onSubmit={onSave}
      onNextButtonHover={e => setHighlightMissingFields(e)}
      showPagesFilter={false}
    />
  );
}

EditAnnotationsModal.propTypes = {
  data: PropTypes.shape({
    patientId: PropTypes.string.isRequired,
  }),
  onCancel: PropTypes.func,
  getAnnotations: PropTypes.func.isRequired,
  addAnnotation: PropTypes.func.isRequired,
  editAnnotation: PropTypes.func.isRequired,
  showNotification: PropTypes.func,
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  closeModalWithNextAction: () => {
    dispatch(closeModal(EDIT_ANNOTATIONS_MODAL_NAME));
    if (ownProps.data.onSuccess) ownProps.data.onSuccess();
  },
  getAnnotations: patientId => dispatch(actions.getAnnotations(patientId)),
  addAnnotation: (patientId, data) => dispatch(actions.addAnnotation(patientId, data)),
  editAnnotation: (patientId, annotationId, data) => dispatch(actions.editAnnotation(patientId, annotationId, data)),
  deleteAnnotation: (patientId, annotationId) => dispatch(actions.deleteAnnotation(patientId, annotationId)),
  showNotification: (message, timeout, isError) => dispatch(notificationActions.show(message, timeout, isError)),
});

export default connect(null, mapDispatchToProps)(EditAnnotationsModal);
